/**
 * Maps `items` through `task`, running at most `limit` tasks at once and
 * starting them in item order. Once a task fails no further task starts,
 * and the call settles only when no task is still running, so nothing it
 * started outlives it.
 *
 * @param items - The inputs, one task each.
 * @param limit - The most tasks running at once; a positive integer.
 * @param task - Runs one item, given the item and its index.
 * @returns The results, in item order.
 * @throws The first failure, once every running task has settled.
 */
export async function mapWithConcurrency<T, R>(
    items: readonly T[],
    limit: number,
    task: (item: T, index: number) => Promise<R>
): Promise<R[]> {
    if (!Number.isInteger(limit) || limit < 1) {
        throw new Error(
            `mapWithConcurrency: limit must be a positive integer, got ${limit}.`
        );
    }

    const results = new Array<R>(items.length);
    const failures: unknown[] = [];
    let next = 0;

    const worker = async (): Promise<void> => {
        // `next++` claims an index synchronously before the first await, so
        // no item runs twice.
        while (next < items.length && failures.length === 0) {
            const index = next++;
            try {
                results[index] = await task(items[index], index);
            } catch (error) {
                failures.push(error);
            }
        }
    };

    await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, () => worker())
    );
    if (failures.length > 0) {
        throw failures[0];
    }
    return results;
}
